import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import styled, { keyframes } from 'styled-components'

const fadeIn = keyframes`
 from { opacity: 0; }
 to { opacity: 1; }
`

interface ModalStyle {
  background?: string
  animationDuration?: number
}

const Overlay = styled.div<ModalStyle>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100vw;
  z-index: 99999;
  background: ${({ background }) => background || 'rgba(0, 0, 0, 0.7)'};
  transition: opacity 0.5s;
  animation: ${fadeIn} ${({ animationDuration }) => animationDuration ?? 0.5}s;

  > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

type Children = (
  closeProps: React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
) => React.ReactNode

interface ModalProps {
  children: Children
  onClose: () => void
  onBeforeClose?: () => void
  className?: string
  style?: ModalStyle
}

const Modal = ({
  children,
  onClose,
  onBeforeClose,
  className,
  style = {},
}: ModalProps) => {
  const [opacity, setOpacity] = useState(1)
  const close = () => {
    setOpacity(0)
    setTimeout(onClose, 500)
    onBeforeClose?.()
  }

  return (
    <Overlay
      style={{ opacity }}
      onClick={(e: React.MouseEvent) => {
        if (e.currentTarget === e.target) close()
      }}
      {...style}
    >
      <div className={className}>{children({ onClick: close })}</div>
    </Overlay>
  )
}

type Props = {
  children: Children
  open: boolean
  onClose: () => void
  onBeforeClose?: () => void
  className?: string
  style?: ModalStyle
}

const OVERLAY_ID = 'react-modal-overlay'

const ModalRenderer = ({
  children,
  open,
  onClose,
  onBeforeClose,
  className,
  style,
}: Props) => {
  useEffect(() => {
    let container = document.getElementById(OVERLAY_ID)
    if (open && !container) {
      container = document.createElement('DIV')
      container.id = OVERLAY_ID
      document.body.appendChild(container)
    }
    if (open) {
      ReactDOM.render(
        <Modal
          onClose={onClose}
          onBeforeClose={onBeforeClose}
          className={className}
          style={style}
        >
          {children}
        </Modal>,
        container
      )
      return () => container.remove()
    }
  }, [open])

  return <></>
}

export default ModalRenderer
